import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiCalendar, FiSave, FiX, FiTrash2, FiCheck } from 'react-icons/fi';
import { prospectService } from '../../services';
import { Modal } from '../../components/UI/Modal';
import './ProspectoForm.scss';

const ProspectoForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    business_name: '',
    client_type: 'betel_natural_tienda',
    status: 'en_proceso',
    assigned_user_id: 1,
    status_date: new Date().toISOString().split('T')[0],
    status_reason: '',
    notes: '',
    source: '',
    tags: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [followUpData, setFollowUpData] = useState({
    activity_type: 'follow_up',
    title: '',
    description: '',
    next_follow_up_date: '',
    next_follow_up_time: ''
  });

  // Cargar datos si es edición
  useEffect(() => {
    if (isEditing) {
      loadProspectData();
    }
  }, [id]);

  const loadProspectData = async () => {
    try {
      setIsLoading(true);
      const response = await prospectService.getProspectById(id);

      // Extraer los datos del response (response.data contiene el prospecto)
      const prospect = response.data;

      console.log('Prospecto cargado:', prospect);

      setFormData({
        full_name: prospect.full_name || '',
        email: prospect.email || '',
        phone: prospect.phone || '',
        business_name: prospect.business_name || '',
        client_type: prospect.client_type || 'betel_natural_tienda',
        status: prospect.status || 'en_proceso',
        assigned_user_id: prospect.assigned_user_id || 1,
        status_date: prospect.status_date || new Date().toISOString().split('T')[0],
        status_reason: prospect.status_reason || '',
        notes: prospect.notes || '',
        source: prospect.source || '',
        tags: prospect.tags || ''
      });
    } catch (err) {
      setError('Error al cargar el prospecto');
      console.error('Error cargando prospecto:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setIsLoading(true);
      setError(null);

      // Preparar el body con solo los campos requeridos por el API
      const requestBody = {
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone || '',
        business_name: formData.business_name || '',
        client_type: formData.client_type,
        assigned_user_id: formData.assigned_user_id
      };

      // Si es edición, agregar campos adicionales opcionales
      if (isEditing) {
        if (formData.status) requestBody.status = formData.status;
        if (formData.status_reason) requestBody.status_reason = formData.status_reason;
        if (formData.notes) requestBody.notes = formData.notes;
      }

      console.log('Enviando prospecto:', requestBody);

      if (isEditing) {
        await prospectService.updateProspect(id, requestBody);
      } else {
        await prospectService.createProspect(requestBody);
      }

      navigate('/system/seguimiento');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar el prospecto');
      console.error('Error completo:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProgramarSeguimiento = async () => {
    if (!isEditing) {
      setError('Debes guardar el prospecto primero antes de programar seguimientos');
      return;
    }
    setShowFollowUpModal(true);
    await loadActivities();
  };

  const loadActivities = async () => {
    try {
      setLoadingActivities(true);
      const response = await prospectService.getActivities(id);
      setActivities(response.data || []);
      console.log('Actividades cargadas:', response.data);
    } catch (err) {
      console.error('Error cargando actividades:', err);
      setActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  };

  const handleFollowUpChange = (e) => {
    const { name, value } = e.target;
    setFollowUpData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getActivityTypeLabel = (type) => {
    const labels = {
      follow_up: 'Seguimiento',
      call: 'Llamada',
      meeting: 'Reunión',
      email: 'Email',
      note: 'Nota',
      other: 'Otro'
    };
    return labels[type] || type;
  };

  const formatActivityDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSaveFollowUp = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Validaciones
      if (!followUpData.title || !followUpData.next_follow_up_date || !followUpData.next_follow_up_time) {
        setError('Título, fecha y hora son requeridos');
        setIsLoading(false);
        return;
      }

      // Combinar fecha y hora
      const followUpDateTime = `${followUpData.next_follow_up_date}T${followUpData.next_follow_up_time}:00`;

      const activityBody = {
        activity_type: followUpData.activity_type,
        title: followUpData.title,
        description: followUpData.description || '',
        next_follow_up_date: followUpDateTime
      };

      console.log('Programando seguimiento:', activityBody);

      await prospectService.addActivity(id, activityBody);

      // Limpiar formulario y cerrar modal
      setFollowUpData({
        activity_type: 'follow_up',
        title: '',
        description: '',
        next_follow_up_date: '',
        next_follow_up_time: ''
      });

      // Mostrar mensaje de éxito
      alert('Seguimiento programado exitosamente');

      // Recargar las actividades para mostrar la nueva
      await loadActivities();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al programar seguimiento');
      console.error('Error programando seguimiento:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteActivity = async (activityId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta actividad?')) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      await prospectService.deleteActivity(id, activityId);

      alert('Actividad eliminada exitosamente');
      await loadActivities();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al eliminar actividad');
      console.error('Error eliminando actividad:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompleteActivity = async (activityId) => {
    try {
      setIsLoading(true);
      setError(null);

      await prospectService.completeActivity(id, activityId);

      alert('Actividad marcada como completada');
      await loadActivities();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al completar actividad');
      console.error('Error completando actividad:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && isEditing) {
    return <div className="loading-container">Cargando prospecto...</div>;
  }

  return (
    <div className="prospecto-form-page">
      <div className="form-header">
        <button className="back-button" onClick={() => navigate('/system/seguimiento')}>
          <FiArrowLeft /> VOLVER
        </button>
        <h1 className="form-title">
          {isEditing ? 'EDITAR PROSPECTO' : 'NUEVO PROSPECTO'}
        </h1>
      </div>

      {error && (
        <div className="error-alert">
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="prospecto-form">
        <div className="form-columns">
          {/* COLUMNA IZQUIERDA */}
          <div className="form-column form-column--left">
            <div className="form-group">
              <label className="form-label">Nombre del Cliente *</label>
              <input
                type="text"
                name="full_name"
                className="form-input"
                value={formData.full_name}
                onChange={handleChange}
                required
                placeholder="Ingrese el nombre completo"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Email *</label>
              <input
                type="email"
                name="email"
                className="form-input"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="correo@ejemplo.com"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Responsable del Seguimiento</label>
              <input
                type="text"
                name="assigned_user_id"
                className="form-input"
                value="Usuario Actual"
                disabled
              />
            </div>

            <div className="form-group">
              <label className="form-label">Status *</label>
              <select
                name="status"
                className="form-select"
                value={formData.status}
                onChange={handleChange}
                required
              >
                <option value="en_proceso">EN PROCESO</option>
                <option value="alcanzado">ALCANZADO</option>
                <option value="perdido">PERDIDO</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Razón del Status</label>
              <textarea
                name="status_reason"
                className="form-textarea"
                value={formData.status_reason}
                onChange={handleChange}
                rows="3"
                placeholder="Ingrese la razón del estado actual"
              />
            </div>

            <button type="submit" className="btn btn-primary btn-submit" disabled={isLoading}>
              <FiSave />
              {isLoading ? 'GUARDANDO...' : 'DAR DE ALTA'}
            </button>
          </div>

          {/* COLUMNA DERECHA */}
          <div className="form-column form-column--right">
            <div className="form-group">
              <label className="form-label">Fecha</label>
              <input
                type="date"
                name="status_date"
                className="form-input"
                value={formData.status_date}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Nombre del Negocio</label>
              <input
                type="text"
                name="business_name"
                className="form-input"
                value={formData.business_name}
                onChange={handleChange}
                placeholder="Nombre de la empresa"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Teléfono</label>
              <input
                type="tel"
                name="phone"
                className="form-input"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+52 123 456 7890"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Tipo de Cliente *</label>
              <select
                name="client_type"
                className="form-select"
                value={formData.client_type}
                onChange={handleChange}
                required
              >
                <option value="betel_natural_tienda">Betel Natural Tienda</option>
                <option value="betel_natural_distribuidor">Betel Natural Distribuidor</option>
                <option value="naturalandlab">Naturaland Lab</option>
              </select>
            </div>

            <button
              type="button"
              className="btn btn-secondary btn-schedule"
              onClick={handleProgramarSeguimiento}
              disabled={!isEditing}
              title={!isEditing ? 'Guarda el prospecto primero' : 'Programar seguimiento'}
            >
              <FiCalendar />
              PROGRAMAR SEGUIMIENTO
            </button>
            {!isEditing && (
              <small style={{ color: '#999', fontSize: '12px', marginTop: '-10px' }}>
                * Guarda el prospecto primero para programar seguimientos
              </small>
            )}

            <div className="form-group">
              <label className="form-label">Notas</label>
              <textarea
                name="notes"
                className="form-textarea"
                value={formData.notes}
                onChange={handleChange}
                rows="5"
                placeholder="Notas adicionales sobre el prospecto"
              />
            </div>
          </div>
        </div>
      </form>

      {/* Modal: Programar Seguimiento */}
      <Modal
        isOpen={showFollowUpModal}
        onClose={() => setShowFollowUpModal(false)}
        title="Programar Seguimiento"
        size="large"
        contentClassName="follow-up-modal-content"
      >
        <div className="follow-up-modal">
          {/* Tabla de actividades existentes */}
          <div className="activities-section">
            <h3 className="section-title">Seguimientos Programados</h3>

            {loadingActivities ? (
              <div className="loading-text">Cargando actividades...</div>
            ) : activities.length === 0 ? (
              <div className="empty-activities">
                <p>No hay seguimientos programados para este prospecto</p>
              </div>
            ) : (
              <div className="activities-table-container">
                <table className="activities-table">
                  <thead>
                    <tr>
                      <th>Tipo</th>
                      <th>Título</th>
                      <th>Descripción</th>
                      <th>Fecha Programada</th>
                      <th className="text-center">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activities.map((activity) => (
                      <tr key={activity.id}>
                        <td>
                          <span className={`activity-type-badge activity-type-${activity.activity_type}`}>
                            {getActivityTypeLabel(activity.activity_type)}
                          </span>
                        </td>
                        <td><strong>{activity.title}</strong></td>
                        <td className="activity-description">
                          {activity.description || '-'}
                        </td>
                        <td>{formatActivityDate(activity.next_follow_up_date)}</td>
                        <td className="activity-actions">
                          {activity.completed_at || activity.is_completed ? (
                            <span className="completed-badge">COMPLETADA</span>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="action-btn action-btn--complete"
                                onClick={() => handleCompleteActivity(activity.id)}
                                title="Marcar como completada"
                                disabled={isLoading}
                              >
                                <FiCheck />
                              </button>
                              <button
                                type="button"
                                className="action-btn action-btn--delete"
                                onClick={() => handleDeleteActivity(activity.id)}
                                title="Eliminar actividad"
                                disabled={isLoading}
                              >
                                <FiTrash2 />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Separador */}
          <div className="section-separator"></div>

          {/* Formulario para nueva actividad */}
          <div className="new-activity-section">
            <h3 className="section-title">Programar Nuevo Seguimiento</h3>

          <div className="form-group">
            <label className="form-label">Tipo de Actividad *</label>
            <select
              name="activity_type"
              className="form-select"
              value={followUpData.activity_type}
              onChange={handleFollowUpChange}
              required
            >
              <option value="follow_up">Seguimiento</option>
              <option value="call">Llamada Telefónica</option>
              <option value="meeting">Reunión</option>
              <option value="email">Email</option>
              <option value="note">Nota</option>
              <option value="other">Otro</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Título *</label>
            <input
              type="text"
              name="title"
              className="form-input"
              value={followUpData.title}
              onChange={handleFollowUpChange}
              placeholder="Ej: Llamada de seguimiento"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Fecha *</label>
              <input
                type="date"
                name="next_follow_up_date"
                className="form-input"
                value={followUpData.next_follow_up_date}
                onChange={handleFollowUpChange}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Hora *</label>
              <input
                type="time"
                name="next_follow_up_time"
                className="form-input"
                value={followUpData.next_follow_up_time}
                onChange={handleFollowUpChange}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Descripción</label>
            <textarea
              name="description"
              className="form-textarea"
              value={followUpData.description}
              onChange={handleFollowUpChange}
              rows="4"
              placeholder="Detalles de la actividad de seguimiento..."
            />
          </div>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowFollowUpModal(false)}
              disabled={isLoading}
            >
              <FiX />
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSaveFollowUp}
              disabled={isLoading}
            >
              <FiCalendar />
              {isLoading ? 'Guardando...' : 'Programar'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ProspectoForm;
